// React already imported with hooks above
import { useDispatch, useSelector } from "react-redux";
import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import {
  setMapStateAction,
  setDefaultMapViewAction,
} from "../../redux/slices/mapbox/mapSlice";
import "mapbox-gl/dist/mapbox-gl.css";
import { styled, Tooltip } from "@mui/material";
import Box from "@mui/material/Box";
import Switch from "@mui/material/Switch"; 
import { retainMapStateAfterRefreshAction } from "../../redux/slices/layers";
import { useNavigate, useLocation } from "react-router-dom";
import { setCesiumView } from "redux/slices/mapbox/mapSlice";
import { checkMapLayersSource, addPanoPointsToMap } from 'utils/layers/mapUtils';
import PrivateRequestBase from "redux/sagas/requests/rootRequest/PrivateRequestBase";
import store from "../../redux/store";

const BoxStyled = styled(Box)(({ theme }) => ({
  height: "calc(100vh - 64px)",
  overflow: "hidden",
  [theme.breakpoints.down("sm")]: {
    height: "calc(100vh - 54px)",
  },
}));

const ErrorBanner = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "72px",
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: 1100,
  background: "#ffeded",
  color: "#842029",
  border: "1px solid #f5c2c7",
  borderRadius: "6px",
  padding: theme.spacing(1),
  maxWidth: "90%",
  fontSize: "0.875rem",
}));

const ModeLabel = styled('span')(({ theme, isChecked }) => ({
  fontWeight: 'bold',
  color: theme.palette?.secondary?.main || theme.palette.text.primary, 
  marginRight: isChecked ? '0' : '8px', 
  marginLeft: isChecked ? '8px' : '0',
}));

const ToolbarStyled = styled(Box)(({ theme, is3dUploaded }) => ({
  position: "absolute",
  top: "10px", 
  right: "50px", 
  zIndex: 1000, 
  backgroundColor: is3dUploaded ? theme.palette.primary.dark : "grey",
  padding: theme.spacing(0.5), 
  borderRadius: "8px", 
}));

const MapComponent = ({ selectedProjectId }) => {
  const map = useRef(null);
  const mapContainer = useRef(null);
  const [mapInstanceId, setMapInstanceId] = useState(0);
  const mapState = useSelector((state) => state.mapState);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const history = React.useMemo(() => ({
    push: (to) => navigate(to),
    replace: (to) => navigate(to, { replace: true }),
    goBack: () => navigate(-1)
  }), [navigate]);
  const [checked, setChecked] = useState(false); 
  const [cesium, setCesium] = useState(false);
  const [is3dUploaded, set3dUploaded] = useState(false);
  const [mapError, setMapError] = useState(null);
  const flightData = useSelector((state) => state.seeList);

  
  const selectedFlight = useSelector((state) => state.flightSelected);
  // const flights = useSelector((state) => state.flights); 
  const flightUuId = selectedFlight?.uuid;
  
  const tilesApiUrl = flightUuId 
    ? `${process.env.REACT_APP_BACKEND_URL}/api/3d_tileset_file/${flightUuId}/tileset.json`
    : null;

// Avoid forcing resize during mount; Mapbox handles initial sizing on its own

  useEffect(() => {
    const checkTilesetFile = async () => {
      if (selectedFlight && tilesApiUrl) {
        PrivateRequestBase("get", tilesApiUrl)
          .then((response) => {
            // Quietly detect availability; do not throw to avoid noisy console
            if (response?.data?.asset) {
              set3dUploaded(true);
            } else {
              set3dUploaded(false);
            }
          })
          .catch(() => {
            // 3D tileset is optional; silently disable switch
            set3dUploaded(false);
          });
      }
    };

    checkTilesetFile();
  }, [selectedFlight, tilesApiUrl]);

  
const switchtoCesium = () => {
  setCesium((prevCesium) => {
    const updatedCesium = !prevCesium;
    dispatch(setCesiumView(updatedCesium));
    if(!updatedCesium) {
      console.log('updatedCesium', !updatedCesium);
      dispatch(selectFlightAction({ flight : selectedFlight }));
    }
    return updatedCesium;
  });
};

  useEffect(() => {
    if (!selectedProjectId || !map.current) return;

    const handleIdle = () => {
      try {
        // Get current state from Redux store inside the handler to avoid stale closures
        const currentState = store.getState();
        if (!currentState) return;
        
        const currentMapState = currentState.mapState;
        if (!currentMapState || typeof currentMapState !== 'object') return;
        
        // Defensive check: mapState doesn't have a 'projects' property in its initial state
        // This code may be legacy or needs to use projectList.data instead
        // Check if projects property exists and is an array before accessing
        if (!('projects' in currentMapState)) return;
        
        const projects = currentMapState.projects;
        if (projects === undefined || projects === null) return;
        if (!Array.isArray(projects)) return;
        if (projects.length === 0) return;
        
        // Additional safety: ensure find method exists (should always exist on arrays, but just in case)
        if (typeof projects.find !== 'function') return;
        
        // Now safe to call find
        const selectedProjectFlights = projects.find(
          (project) => project && project.id === selectedProjectId
        )?.flights;

        // Remove markers if no flights are present for the selected project
        if (!selectedProjectFlights || selectedProjectFlights.length === 0) {
          if (map.current && map.current._markers) {
            Object.values(map.current._markers).forEach((marker) => {
              marker.remove();
            });
          }
        }
      } catch (error) {
        // Silently handle any errors to prevent breaking the map
        console.error('Error in map idle handler:', error);
      }
    };

    map.current.on("idle", handleIdle);

    // Cleanup: remove the event listener when dependencies change or component unmounts
    return () => {
      if (map.current) {
        map.current.off("idle", handleIdle);
      }
    };
  }, [selectedProjectId]);

  const mapCheck = () => {
    const isLoaded = map.current.loaded();
    if (isLoaded) {
      dispatch(retainMapStateAfterRefreshAction({ history: history }));
    } else {
      mapCheck();
    }
  };

  useEffect(() => {
    if (map.current) return;
    if (!mapState?.mapView) return; // only init when map view is visible

    // Basic runtime diagnostics for missing token or container
    const token = process.env.REACT_APP_MAPBOX_PUBLIC_ACCESS_TOKEN;
    if (!token) {
      const message = "Mapbox token missing: set REACT_APP_MAPBOX_PUBLIC_ACCESS_TOKEN in your .env and restart dev server.";
      console.error(message);
      setMapError(message);
      return;
    }
    if (!mapContainer.current) {
      const message = "Map container not available. The map root ref is null.";
      console.error(message);
      setMapError(message);
      return;
    }

    // Wait until container has size before creating the map (handles refresh with hidden container)
    const ensureContainerSized = () =>
      new Promise((resolve) => {
        let attempts = 0;
        const check = () => {
          attempts += 1;
          const { clientWidth, clientHeight } = mapContainer.current || {};
          if (clientWidth > 0 && clientHeight > 0) {
            resolve(true);
            return;
          }
          if (attempts > 20) {
            console.warn("Map container still has zero size after waiting. Proceeding anyway.");
            resolve(true);
            return;
          }
          requestAnimationFrame(check);
        };
        check();
      });

    let isCancelled = false;

    (async () => {
      await ensureContainerSized();
      if (isCancelled || map.current) return;

    const mapData = {
      container: mapContainer.current,
      style: {
        version: 8,
        sources: {
          "raster-tiles": {
            type: "raster",
            url: "mapbox://mapbox.satellite",
            tileSize: 256,
          },
        },
        layers: [
          {
            id: "simple-tiles",
            type: "raster",
            source: "raster-tiles",
            minzoom: 0,
            maxzoom: 22,
          },
        ],
      },
      center: [mapState.lng, mapState.lat],
      zoom: mapState.zoom,
    };
      mapboxgl.accessToken = token;
      map.current = new mapboxgl.Map(mapData);
      map.current.addControl(new mapboxgl.NavigationControl());
      window.map = map.current;
      setMapInstanceId(Date.now());

      map.current.on("error", (e) => {
        console.error("Mapbox GL JS error:", e?.error || e);
        if (!mapError) {
          setMapError(e?.error?.message || "An error occurred initializing the map.");
        }
      });
    })();

    return () => {
      isCancelled = true;
      if (map.current) {
        try {
          map.current.remove();
        } catch (_) {
          // ignore remove errors from already-destroyed maps
        }
        map.current = null;
        if (typeof window !== "undefined") {
          window.map = null;
        }
      }
      setMapInstanceId(0);
    };
  }, [mapState?.mapView]);

  useEffect(() => {
    if (!map.current || !mapInstanceId) return;
    const currentMap = map.current;

    const onceIdle = () => {
      checkMapLayersSource(flightData?.data, flightData?.panoImages);
    };

    const handleIdle = () => {
      try {
        const center = currentMap.getCenter();
        const zoom = currentMap.getZoom();
        const currentState = store.getState()?.mapState;
        if (
          !currentState ||
          center.lng !== currentState.lng ||
          center.lat !== currentState.lat ||
          zoom !== currentState.zoom
        ) {
          dispatch(
            setMapStateAction({
              lng: center.lng,
              lat: center.lat,
              zoom,
            })
          );
        }
      } catch (_) {
        // ignore state sync errors
      }
    };

    const handleRemove = () => {
      console.log("A remove event occurred.");
    };

    currentMap.once("idle", onceIdle);
    currentMap.on("idle", handleIdle);
    currentMap.on("remove", handleRemove);
    currentMap.on("load", mapCheck);

    return () => {
      try { currentMap.off("idle", handleIdle); } catch (_) {}
      try { currentMap.off("remove", handleRemove); } catch (_) {}
      try { currentMap.off("load", mapCheck); } catch (_) {}
    };
  }, [mapInstanceId, flightData?.data, flightData?.panoImages, dispatch]);

  // Ensure map lays out correctly when becoming visible after being hidden
  useEffect(() => {
    if (!map.current) return;
    if (mapState?.mapView) {
      // Defer to next tick so styles/layout settle
      setTimeout(() => {
        try {
          map.current.resize();
        } catch (e) {
          // ignore
        }
      }, 0);
    }
  }, [mapState?.mapView, mapInstanceId]);

  // Re-add pano points when map becomes visible again (e.g., after closing pano overlay)
  useEffect(() => {
    if (!map.current || !mapState?.mapView) return;
    const currentMap = map.current;

    const addPoints = () => {
      addPanoPointsToMap(flightData?.panoImages, currentMap);
    };

    if (currentMap.isStyleLoaded && currentMap.isStyleLoaded()) {
      addPoints();
      return;
    }

    currentMap.once("styledata", addPoints);
    currentMap.once("load", addPoints);
    return () => {
      try { currentMap.off("styledata", addPoints); } catch (_) {}
      try { currentMap.off("load", addPoints); } catch (_) {}
    };
  }, [mapState?.mapView, mapInstanceId, flightData?.panoImages]);

  // Resize map on window resize and when tab becomes visible again
  useEffect(() => {
    if (!map.current) return;
    const onResize = () => {
      try {
        map.current && map.current.resize();
      } catch (e) {
        // ignore
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        onResize();
      }
    };
    window.addEventListener("resize", onResize);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("resize", onResize);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [mapInstanceId]);

  return (
    <>
      <BoxStyled ref={mapContainer} />
      {/* {mapError && (
        <ErrorBanner>
          {mapError}
        </ErrorBanner>
      )} */}
      {location.pathname !== "/project" && (
        <ToolbarStyled is3dUploaded={is3dUploaded}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <ModeLabel isChecked={false} isDisabled={!is3dUploaded}>
              2D
            </ModeLabel>

            <Tooltip title={!is3dUploaded ? '3D model not uploaded yet' : ''}>
              <span> 
                <Switch
                  checked={checked}
                  onChange={switchtoCesium}
                  color="primary"
                  disabled={!is3dUploaded}
                  sx={{
                    opacity: is3dUploaded ? 1 : 0.5,
                    pointerEvents: is3dUploaded ? 'auto' : 'none',
                  }}
                />
              </span>
            </Tooltip>

            <ModeLabel isChecked={true} isDisabled={!is3dUploaded}>
              3D
            </ModeLabel>
          </div>
        </ToolbarStyled>
      )}

    </>
  );
};

export default MapComponent;
